package com.merkost.handpass.sharedweb.theme

import com.varabyte.kobweb.compose.css.CSSTimeNumericValue
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.rotate
import com.varabyte.kobweb.compose.ui.modifiers.scale
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.translateX
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import kotlinx.browser.document
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.deg
import org.jetbrains.compose.web.css.percent

object Ease {
    val Standard: AnimationTimingFunction get() = MotionEaseStandard.value()
    val Out: AnimationTimingFunction get() = MotionEaseOut.value()
    val Linear: AnimationTimingFunction get() = MotionEaseLinear.value()
}

object Dur {
    val Press: CSSTimeNumericValue get() = MotionPress.value()
    val Fast: CSSTimeNumericValue get() = MotionFast.value()
    val Normal: CSSTimeNumericValue get() = MotionNormal.value()
    val Slow: CSSTimeNumericValue get() = MotionSlow.value()
    val FavoritePop: CSSTimeNumericValue get() = MotionFavoritePop.value()
    val Spin: CSSTimeNumericValue get() = MotionSpin.value()
    val Draw: CSSTimeNumericValue get() = MotionDraw.value()
    val Shimmer: CSSTimeNumericValue get() = MotionShimmer.value()
    val Celebration: CSSTimeNumericValue get() = MotionCelebration.value()
    val LongDelay: CSSTimeNumericValue get() = MotionLongDelay.value()
    val Reveal: CSSTimeNumericValue get() = MotionReveal.value()
}

object WebMotion {
    val PressScale: Number get() = MotionPressScale.value()
    val ModalScale: Number get() = MotionModalScale.value()
    val ControlFocusScale: Number get() = MotionControlFocusScale.value()
    val RevealScale: Number get() = MotionRevealScale.value()
    val SubtleHoverScale: Number get() = MotionSubtleHoverScale.value()
    val EditorialHoverScale: Number get() = MotionEditorialHoverScale.value()
    val ChartHoverScale: Number get() = MotionChartHoverScale.value()
    val ChartEntryInitialScale: Number get() = MotionChartEntryInitialScale.value()
    val AccentBarScale: Number get() = MotionAccentBarScale.value()
    val ImageHoverScale: Number get() = MotionImageHoverScale.value()
    val FavoritePopScale: Number get() = MotionFavoritePopScale.value()
    val ShareInitialScale: Number get() = MotionShareInitialScale.value()
    val FeedbackInitialScale: Number get() = MotionFeedbackInitialScale.value()
    val FeedbackOvershootScale: Number get() = MotionFeedbackOvershootScale.value()
    val FeedbackPulseScale: Number get() = MotionFeedbackPulseScale.value()
    val SoldBadgeInitialScale: Number get() = MotionSoldBadgeInitialScale.value()
    val SoldBadgeInitialTilt: Number get() = MotionSoldBadgeInitialTilt.value()
    val SoldBadgeActiveTilt: Number get() = MotionSoldBadgeActiveTilt.value()
    val RevealShift get() = MotionRevealShift.value()
}

object WebTransitions {
    val Fast get() = "${Dur.Fast} ${Ease.Standard}"
    val Normal get() = "${Dur.Normal} ${Ease.Standard}"
    val Slow get() = "${Dur.Slow} ${Ease.Standard}"

    fun colors(duration: CSSTimeNumericValue = Dur.Fast): Transition =
        Transition.group(listOf("background-color", "border-color", "color", "box-shadow"), duration, Ease.Standard)

    fun surface(duration: CSSTimeNumericValue = Dur.Fast): Transition =
        Transition.group(listOf("background-color", "border-color", "box-shadow", "transform", "scale"), duration, Ease.Standard)

    fun surfaceAction(duration: CSSTimeNumericValue = Dur.Fast): Transition =
        Transition.group(listOf("background-color", "border-color", "color", "box-shadow", "transform", "scale"), duration, Ease.Standard)

    fun transform(duration: CSSTimeNumericValue = Dur.Fast): Transition =
        Transition.group(listOf("transform", "scale", "translate"), duration, Ease.Standard)

    fun opacity(duration: CSSTimeNumericValue = Dur.Fast): Transition =
        Transition.of("opacity", duration, Ease.Standard)
}

fun Modifier.transitionColors(duration: CSSTimeNumericValue = Dur.Fast): Modifier = transition(WebTransitions.colors(duration))

fun Modifier.transitionSurface(duration: CSSTimeNumericValue = Dur.Fast): Modifier = transition(WebTransitions.surface(duration))

fun Modifier.transitionSurfaceAction(duration: CSSTimeNumericValue = Dur.Fast): Modifier = transition(WebTransitions.surfaceAction(duration))

fun Modifier.transitionTransform(duration: CSSTimeNumericValue = Dur.Fast): Modifier = transition(WebTransitions.transform(duration))

fun Modifier.transitionReveal(duration: CSSTimeNumericValue = Dur.Reveal): Modifier =
    transition(Transition.group(listOf("opacity", "transform"), duration, Ease.Out))

object Shadow {
    val Card get() = ShadowCard.value()
    val CardHover get() = ShadowCardHover.value()
    val Dropdown get() = ShadowDropdown.value()
    val Modal get() = ShadowModal.value()
    val Pop get() = ShadowPop.value()
}

object AnimNames {
    const val Shimmer = "shimmer"
    const val ToastIn = "toast-in"
    const val ModalIn = "modal-in"
    const val Spin = "spin"
    const val DrawerIn = "drawer-in"
    const val BackdropFade = "backdrop-fade"
    const val AccentBarIn = "accent-bar-in"
    const val ToastOut = "toast-out"
}

@InitSilk
fun initWebAnim(ctx: InitSilkContext) {
    if (document.getElementById("handpass-shared-reduced-motion") == null) {
        val style = document.createElement("style")
        style.setAttribute("id", "handpass-shared-reduced-motion")
        style.textContent =
            ".hp-img{opacity:0;transition:opacity ${Dur.Reveal} ${Ease.Standard}}" +
                ".hp-img.hp-loaded{opacity:1}" +
                ".hp-img.hp-img-error{visibility:hidden}" +
                ".hp-focus-surface,.hp-focus-input{transition:border-color ${Dur.Fast} ${Ease.Standard},box-shadow ${Dur.Fast} ${Ease.Standard}}" +
                ".hp-focus-surface:focus-within{--hp-focus-border:${OnSurface.value()};--hp-focus-shadow:0 0 0 ${WebSize.P2.value()} ${OnSurface.value()},${Shadow.Card}}" +
                ".hp-focus-input:focus-visible{border-color:${OnSurface.value()}!important;box-shadow:0 0 0 ${WebSize.P2.value()} ${OnSurface.value()};outline:none}" +
                ".hp-pressable{transition:transform ${Dur.Press} ${Ease.Standard}}" +
                ".hp-pressable:active{transform:scale(${WebMotion.PressScale})}" +
                ".hp-hit-expand{position:relative}" +
                ".hp-hit-expand::before{content:'';position:absolute;inset:calc(-1 * ${WebSize.P6.value()})}" +
                "@media (prefers-reduced-motion:reduce){*,*::before,*::after{animation:none!important;animation-duration:${MotionReduced.value()}!important;animation-iteration-count:1!important;transition:none!important;transition-duration:${MotionReduced.value()}!important;scroll-behavior:auto!important}.hp-img{opacity:1!important;transform:none!important}button:active,[role=button]:active,.hp-pressable:active{scale:1!important;transform:none!important}}"
        document.head?.appendChild(style)
    }
    ctx.stylesheet.registerKeyframes(AnimNames.Shimmer) {
        from { Modifier.styleModifier { property("background-position", "200% 0") } }
        to { Modifier.styleModifier { property("background-position", "-200% 0") } }
    }
    ctx.stylesheet.registerKeyframes(AnimNames.ToastIn) {
        from {
            Modifier
                .opacity(0)
                .translateX(WebSize.P40.value())
                .scale(WebMotion.PressScale)
        }
        to { Modifier.opacity(1) }
    }
    ctx.stylesheet.registerKeyframes(AnimNames.ModalIn) {
        from {
            Modifier
                .opacity(0)
                .translateY(WebSize.P8.value())
                .scale(WebMotion.ModalScale)
        }
        to { Modifier.opacity(1) }
    }
    ctx.stylesheet.registerKeyframes(AnimNames.Spin) {
        from { Modifier.rotate(0.deg) }
        to { Modifier.rotate(360.deg) }
    }
    ctx.stylesheet.registerKeyframes(AnimNames.DrawerIn) {
        from { Modifier.translateX((-100).percent) }
        to { Modifier.translateX(0.percent) }
    }
    ctx.stylesheet.registerKeyframes(AnimNames.BackdropFade) {
        from { Modifier.opacity(0) }
        to { Modifier.opacity(1) }
    }
    ctx.stylesheet.registerKeyframes(AnimNames.AccentBarIn) {
        from { Modifier.styleModifier { property("transform", "translateY(-50%) scaleY(${WebMotion.AccentBarScale})"); property("opacity", "0") } }
        to { Modifier.styleModifier { property("transform", "translateY(-50%) scaleY(1)"); property("opacity", "1") } }
    }
    ctx.stylesheet.registerKeyframes(AnimNames.ToastOut) {
        from { Modifier.opacity(1) }
        to {
            Modifier
                .opacity(0)
                .translateX(WebSize.P40.value())
                .scale(WebMotion.PressScale)
        }
    }
}
